import React from "react";
import { IMask } from "react-imask";
import { TestRequestSearchResult } from "../../../api/models/TestRequest";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import StatusChip from "../../../components/Chip/StatusChip";
import { CustomTableRowData } from "../../../components/CustomTable";
import WithFlagIconsList from "../../../components/RequestFlags/WithFlagIconsList";
import { useSelectedColumns } from "../../../contexts/SelectedColumnsContext";
import TestRequestOverviewColumns, * as OverviewColumns from "./TestRequestsOverviewColumns";

type RowData = string | number | JSX.Element | undefined;
type Row = CustomTableRowData & { [K in TestRequestOverviewColumns]?: RowData };

const pnsPotentialPipe = IMask.createPipe({ mask: Number, scale: 4, thousandsSeparator: " " });

function useTableData(testRequests: TestRequestSearchResult[], configurable?: boolean, editPriority: boolean = false) {
  const { selectedColumns: selected } = useSelectedColumns();

  const getFixedColumnLabels = (editPriority: boolean) => {
    const baseFixedLabels = OverviewColumns.fixedColumnLabels;
    return editPriority ? baseFixedLabels : baseFixedLabels.filter((label) => label !== "Priority");
  };

  const getConfigurableColumnLabels = (editPriority: boolean) => {
    const baseConfigurableColumns = OverviewColumns.configurableColumnLabels;
    return editPriority ? baseConfigurableColumns : baseConfigurableColumns.filter((label) => label !== "Priority");
  };

  const generateColumns = () => {
    const fixed = getFixedColumnLabels(editPriority);
    const configurableAll = getConfigurableColumnLabels(editPriority);

    return fixed.concat(
      configurable
        ? configurableAll.filter((cn) => selected.findIndex((v) => OverviewColumns.columnsDictionary[v] === cn) > -1)
        : configurableAll,
    );
  };

  const getRowData = (testReq: TestRequestSearchResult, column: TestRequestOverviewColumns): RowData => {
    switch (column) {
      case "priority":
        return editPriority ? testReq.testRequestPriority || " " : "";
      case "assigneeName":
        return testReq.assigneeName || "Unassigned";
      case "pnsPotential":
        return testReq.pnsPotential && testReq.status.state !== "DRAFT"
          ? `${pnsPotentialPipe(String(testReq.pnsPotential?.value))} ${testReq.pnsPotential.currencyCode}`
          : "";
      case "requester":
        return testReq.requester?.userName;
      case "status":
        return <StatusChip status={testReq.status} />;
      case "submissionOrApprovalDate":
        return testReq.submissionOrApprovalDate ? dashToSlash(testReq.submissionOrApprovalDate) : "";
      case "estimatedFinishingDate":
        return testReq.estimatedFinishingDate ? dashToSlash(testReq.estimatedFinishingDate) : "";
      case "deadLine":
        return testReq.deadLine ? dashToSlash(testReq.deadLine) : "";
      case "title":
        return (
          <WithFlagIconsList flags={testReq.flags} gutterSpacing={1.5} tooltipForChildren={testReq.title}>
            {testReq.title}
          </WithFlagIconsList>
        );
      default:
        return column in testReq ? (testReq[column] as RowData) : undefined;
    }
  };

  const getRowBase = (testReq: TestRequestSearchResult): Row => {
    const rowBase: Row = {
      id: testReq.id,
    };
    OverviewColumns.fixedColumns.forEach((fc) => {
      rowBase[fc] = getRowData(testReq, fc);
    });
    return rowBase;
  };

  const getRow = (
    testReq: TestRequestSearchResult,
    configurable: boolean = false,
    editPriority: boolean = true,
  ): Row => {
    const row = getRowBase(testReq);

    // Render all columns, handling 'priority' conditionally
    const getAll = (vc: TestRequestOverviewColumns) => {
      if (vc === "priority" && !editPriority) {
        row[vc] = "N/A";
      } else {
        row[vc] = getRowData(testReq, vc);
      }
    };

    // Render only visible columns, handling 'priority' conditionally
    const getVisible = (vc: TestRequestOverviewColumns) => {
      if (selected.includes(vc)) {
        if (vc === "priority" && !editPriority) {
          row[vc] = "N/A";
        } else {
          row[vc] = getRowData(testReq, vc);
        }
      }
    };
    OverviewColumns.configurableColumns.forEach(configurable ? getVisible : getAll);
    return row;
  };

  const generateRows = (): Row[] => {
    const rows = testRequests.map((testReq: TestRequestSearchResult) => getRow(testReq, configurable));
    return rows;
  };

  return {
    columns: generateColumns(),
    rows: generateRows(),
  };
}

export default useTableData;
